import EngineShell, { type SubTabDef } from '../components/EngineShell';
import DataIntervention, {
  ActionIntervention, MetricTiles, MiniTable, StatusPill, Provenance,
} from '../components/engine/DataIntervention';
import { api } from '../api/client';
import { useState } from 'react';

/**
 * Engine 2 · Sourcing & Procurement (CPG value chain · stage 2).
 * Input-cost + supply-resilience lever. Profit-at-Risk read-view is the lighthouse.
 */

interface ParResp { p5: number; p50: number; p95: number; profit_at_risk_band: string; hedge_priority: string[]; single_supplier_share: number; as_of: string }

interface CoverageRow { commodity: string; coverage_pct: number; target_pct: number; status: string }
interface HedgingResp { coverage_by_commodity: CoverageRow[]; band_state: string }

interface ConsolidateResp { volume_leverage: string; contract_terms: string[]; consolidation_savings_usd_M: number }

interface DefectRow { supplier: string; commodity: string; defect_rate_ppm: number; status: string }
interface DefectResp { defect_rate_by_supplier: DefectRow[]; flags: string[] }

interface SlowMover { commodity: string; days_on_hand: number; action: string }
interface TurnoverResp { turns: number; slow_movers: SlowMover[] }

interface LeadTime { material: string; supplier: string; days: number; status: string }
interface Mitigation { rank: number; action: string; owner: string; lead_time_days: number }
interface ResilienceResp { single_supplier_share: number; lead_times: LeadTime[]; mitigations: Mitigation[] }

const pct = (v: number) => `${v.toFixed(1)}%`;
const moneyM = (v: number) => (v < 0 ? `−$${Math.abs(v).toFixed(1)}M` : `$${v.toFixed(1)}M`);

export default function Engine2Shell() {
  const subTabs: SubTabDef[] = [
    {
      id: 'profit_at_risk_view', name: 'Profit-at-Risk View', tier: 'lighthouse', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<ParResp>
          objective="O2 · watch the input-cost downside band and prioritize hedges"
          workflow={['Pull latest profit-at-risk run', 'Read p5/p50/p95 band', 'Rank hedge priority', 'Watch single-supplier exposure']}
          decisionRules={{ rules: ['Escalate if p5 downside breaches the loss floor', 'Hedge the top-ranked commodity first'] }}
          aiLogic={{ description: "Read-view of the Simulation engine's Monte Carlo run (latest cached result).", modality: 'deterministic', hitlLevel: 'L3 approval gating', moat: 'core' }}
          kpi={{ primary: 'p5/p50/p95 profit band + hedge priority', downstream: 'Drives the hedge book', owner: 'CFO / Procurement' }}
          queryKey={['sourcing', 'par-latest']} fetcher={() => api.get<ParResp>('/api/sourcing/profit-at-risk/latest')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'p5 (downside)', value: moneyM(d.p5), color: 'red' },
                { label: 'p50 (median)', value: moneyM(d.p50), color: d.p50 < 0 ? 'gold' : 'olive' },
                { label: 'p95 (upside)', value: moneyM(d.p95), color: 'olive' },
                { label: 'Single-supplier', value: pct(d.single_supplier_share * 100), color: d.single_supplier_share > 0.55 ? 'red' : 'gold' },
              ]} />
              <div className="small" style={{ marginBottom: 8 }}>Band: <b>{d.profit_at_risk_band}</b></div>
              <div className="small faint" style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>Hedge priority</div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {d.hedge_priority.map((h, i) => <span key={h} className="tag">{i + 1}. {h}</span>)}
              </div>
              <Provenance asOf={d.as_of} />
              <div className="small faint" style={{ marginTop: 8 }}>Full controls in Simulation · Profit-at-Risk.</div>
            </>
          )}
        />
      ),
    },
    {
      id: 'hedging', name: 'Hedging', tier: 'flagship', moat: 'core', enabled: true,
      render: () => (
        <DataIntervention<HedgingResp>
          objective="O2 · hold hedge coverage at target across volatile commodities"
          workflow={['Pull coverage vs target by commodity', 'Flag under-covered inputs', 'Top up the book', 'Track band state']}
          decisionRules={{ rules: ['Amber if coverage below target', 'Red if coverage gap > 20pp'] }}
          aiLogic={{ description: 'Deterministic coverage rollup vs hedge targets.', modality: 'deterministic', hitlLevel: 'L2 exception review', moat: 'core' }}
          kpi={{ primary: 'Coverage % vs target by commodity', owner: 'CFO / Treasury' }}
          queryKey={['sourcing', 'hedging']} fetcher={() => api.get<HedgingResp>('/api/sourcing/hedging')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Band state', value: d.band_state, color: d.band_state === 'red' ? 'red' : d.band_state === 'amber' ? 'gold' : 'olive' },
                { label: 'Commodities', value: String(d.coverage_by_commodity.length), color: 'ink' },
              ]} />
              <MiniTable title="Coverage by commodity" rows={d.coverage_by_commodity} rowKey={(r) => r.commodity}
                columns={[
                  { header: 'Commodity', cell: (r) => <b className="mono">{r.commodity}</b> },
                  { header: 'Coverage', align: 'right', cell: (r) => <span className="mono">{pct(r.coverage_pct)}</span> },
                  { header: 'Target', align: 'right', cell: (r) => <span className="mono faint">{pct(r.target_pct)}</span> },
                  { header: 'Status', align: 'right', cell: (r) => <StatusPill status={r.status} /> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'supplier_consolidation', name: 'Supplier Consolidation', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <SupplierConsolidation />,
    },
    {
      id: 'supplier_defect_rate', name: 'Supplier Defect Rate', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => (
        <DataIntervention<DefectResp>
          objective="O2 · keep incoming defect rates inside spec (hygiene)"
          workflow={['Pull defect ppm by supplier lot', 'Flag the worst lots', 'Open corrective actions', 'Track to closure']}
          decisionRules={{ rules: ['Amber if defect rate > 300ppm', 'Red if > 400ppm'] }}
          aiLogic={{ description: 'Deterministic incoming-quality rollup by supplier lot.', modality: 'deterministic', hitlLevel: 'L1 sampling', moat: 'commodity' }}
          kpi={{ primary: 'Defect ppm by supplier', owner: 'QA / Procurement' }}
          queryKey={['sourcing', 'defect-rate']} fetcher={() => api.get<DefectResp>('/api/sourcing/supplier/defect-rate')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Flagged lots', value: String(d.flags.length), color: d.flags.length ? 'gold' : 'olive' },
                { label: 'Suppliers', value: String(d.defect_rate_by_supplier.length), color: 'ink' },
              ]} />
              <MiniTable title="Defect rate by supplier" rows={d.defect_rate_by_supplier} rowKey={(r, i) => `${r.supplier}-${i}`}
                columns={[
                  { header: 'Supplier', cell: (r) => <b className="small">{r.supplier}</b> },
                  { header: 'Commodity', cell: (r) => <span className="small faint">{r.commodity}</span> },
                  { header: 'Defect', align: 'right', cell: (r) => <span className="mono">{r.defect_rate_ppm}ppm</span> },
                  { header: 'Status', align: 'right', cell: (r) => <StatusPill status={r.status} /> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'inventory_turnover', name: 'Inventory Turnover', tier: 'flagship', moat: 'commodity', enabled: true,
      render: () => (
        <DataIntervention<TurnoverResp>
          objective="O2 · turn inventory fast, clear slow movers (hygiene)"
          workflow={['Compute network turns', 'Identify slow movers by days-on-hand', 'Assign clearance actions']}
          decisionRules={{ rules: ['Flag any input > 60 days on hand', 'Standardize/outsource slow-moving specials'] }}
          aiLogic={{ description: 'Deterministic turns + days-on-hand from inventory state.', modality: 'deterministic', hitlLevel: 'L1 sampling', moat: 'commodity' }}
          kpi={{ primary: 'Turns + slow movers', owner: 'Supply planning' }}
          queryKey={['sourcing', 'turnover']} fetcher={() => api.get<TurnoverResp>('/api/sourcing/inventory-turnover')}
          render={(d) => (
            <>
              <MetricTiles tiles={[
                { label: 'Inventory turns', value: `${d.turns.toFixed(1)}x`, color: 'ink' },
                { label: 'Slow movers', value: String(d.slow_movers.length), color: d.slow_movers.length ? 'gold' : 'olive' },
              ]} />
              <MiniTable title="Slow movers" rows={d.slow_movers} rowKey={(r, i) => `${r.commodity}-${i}`}
                columns={[
                  { header: 'Commodity', cell: (r) => <b className="small">{r.commodity}</b> },
                  { header: 'Days on hand', align: 'right', cell: (r) => <span className="mono" style={{ color: r.days_on_hand > 60 ? 'var(--gold)' : 'var(--ink)' }}>{r.days_on_hand}d</span> },
                  { header: 'Action', cell: (r) => <span className="small faint">{r.action}</span> },
                ]} />
            </>
          )}
        />
      ),
    },
    {
      id: 'resilience', name: 'Supply Resilience', tier: 'flagship', moat: 'core', enabled: true,
      render: () => <Resilience />,
    },
  ];

  return (
    <EngineShell
      engineId={2}
      engineName="Engine 2 · Sourcing & Procurement"
      engineCode="E2"
      subTabs={subTabs}
      backgrounds={[{ id: 'tariff', name: 'Tariff Watcher', status: 'ok' }]}
    />
  );
}

/* Supplier Consolidation — POST with a category */
function SupplierConsolidation() {
  const [category, setCategory] = useState('freight');
  return (
    <ActionIntervention<ConsolidateResp>
      objective="O2 · consolidate spend to win volume leverage and better terms"
      workflow={['Enter a spend category', 'Model volume leverage', 'Negotiate contract terms', 'Bank consolidation savings']}
      decisionRules={{ rules: ['Hold a spot-market escape hatch', 'Cap single-supplier share at 70%'] }}
      aiLogic={{ description: 'Deterministic spend-consolidation model with term scoring.', modality: 'deterministic', hitlLevel: 'L3 approval gating', moat: 'core' }}
      kpi={{ primary: 'Volume leverage + consolidation savings ($M)', owner: 'CFO / Procurement' }}
      initialState={{ category, volume_leverage: 'freight lane leverage is modeled at 14%', consolidation_savings_usd_M: 1.2, contract_terms: ['spot-market escape hatch'] }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Category</div>
          <input className="c2-input" value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 240 }} />
        </>
      }
      runLabel="Model consolidation"
      action={() => api.sourcingConsolidate(category) as Promise<ConsolidateResp>}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Volume leverage', value: d.volume_leverage, color: 'ink' },
            { label: 'Savings', value: moneyM(d.consolidation_savings_usd_M), color: 'olive' },
          ]} />
          <div className="small faint" style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>Contract terms</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {d.contract_terms.map((t) => <span key={t} className="tag">{t}</span>)}
          </div>
        </>
      )}
    />
  );
}

/* Supply Resilience — POST with a scenario */
function Resilience() {
  const [scenario, setScenario] = useState('vobev_outage');
  return (
    <ActionIntervention<ResilienceResp>
      objective="O2 · stress-test single-point failures and stage mitigations"
      workflow={['Enter a disruption scenario', 'Stress-test lead times', 'Rank mitigations by impact', 'Pre-stage the response']}
      decisionRules={{ rules: ['Escalate if single-supplier share > 55%', 'Qualify a backup for every RED lane'] }}
      aiLogic={{ description: 'Deterministic scenario stress-test across the supply graph.', modality: 'deterministic', hitlLevel: 'L4 human decision', moat: 'core' }}
      kpi={{ primary: 'Single-supplier share + lead times + mitigations', owner: 'COO / Procurement' }}
      initialState={{
        single_supplier_share: 0.58,
        lead_times: [{ material: 'cans', supplier: 'Vobev', days: 42, status: 'RED' }],
        mitigations: [{ rank: 1, action: 'qualify backup can lane', owner: 'Procurement', lead_time_days: 28 }],
      }}
      input={
        <>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Scenario</div>
          <input className="c2-input" value={scenario} onChange={(e) => setScenario(e.target.value)} style={{ width: 240 }} />
        </>
      }
      runLabel="Stress-test scenario"
      action={() => api.sourcingResilience(scenario) as Promise<ResilienceResp>}
      render={(d) => (
        <>
          <MetricTiles tiles={[
            { label: 'Single-supplier share', value: pct(d.single_supplier_share * 100), color: d.single_supplier_share > 0.55 ? 'red' : 'gold' },
            { label: 'Lead-time lanes', value: String(d.lead_times.length), color: 'ink' },
          ]} />
          <MiniTable title="Lead times" rows={d.lead_times} rowKey={(r, i) => `${r.material}-${i}`}
            columns={[
              { header: 'Material', cell: (r) => <b className="small">{r.material}</b> },
              { header: 'Supplier', cell: (r) => <span className="small faint">{r.supplier}</span> },
              { header: 'Days', align: 'right', cell: (r) => <span className="mono">{r.days}d</span> },
              { header: 'Status', align: 'right', cell: (r) => <StatusPill status={r.status} /> },
            ]} />
          <MiniTable title="Mitigations" rows={d.mitigations} rowKey={(r) => String(r.rank)}
            columns={[
              { header: 'Rank', cell: (r) => <b className="mono">{r.rank}</b> },
              { header: 'Action', cell: (r) => <span className="small">{r.action}</span> },
              { header: 'Owner', cell: (r) => <span className="small faint">{r.owner}</span> },
              { header: 'Lead time', align: 'right', cell: (r) => <span className="mono">{r.lead_time_days}d</span> },
            ]} />
        </>
      )}
    />
  );
}
